import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ArrowRightIcon, CheckIcon } from "lucide-react";

import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import { Button } from "simplydo/react/ui/button";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";

import { useAppSelector } from "store";

import HomeBlock from "./HomeBlock";

const TaskItem = styled.div`
  padding: 12px 12px;
  border: 1px solid ${({ theme }) => theme.colors.primary.softer};
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;

  &:hover {
    background-color: ${({ theme }) => theme.colors.primary.softer};
    cursor: pointer;
  }
`;

const TaskItemContent = styled.div`
  font-weight: 500;

  color: #000;
`;

const DEFAULT_TASKLIST = {
  completeProfile: true,
  exploreChallenges: true,
  createIdea: true,
  submitIdea: false,
  joinGroup: false,
  setup2fa: false,
};

const TasklistBlock = () => {
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);
  const { t } = useTranslation();
  const org = user?.ownerOrganisation;

  if (!user) return null;

  const enabledTasks = { ...DEFAULT_TASKLIST, ...org?.homepageTasklist };

  const profileComplete = user.engagements.includes("savedProfile");

  const viewedChallenge = user.engagements.includes("viewedChallenge");
  const createdIdea =
    (user.ideaCount && user.ideaCount > 0) ||
    user.engagements.includes("createdIdea");
  const submittedIdea =
    (user.submittedIdeaCount && user.submittedIdeaCount > 0) ||
    user.engagements.includes("submittedIdea");
  const joinedGroup = user.groups.length > 0;
  const setup2fa = !!user.mfaEnabled;
  const hasPassword = !!user.hasPassword;

  const tasks = [
    {
      key: "completeProfile" as const,
      enabled: enabledTasks.completeProfile,
      complete: profileComplete,
      to: "/preferences/profile",
      title: t("home.tasklist.createProfile"),
      subtitle: t("home.tasklist.createProfile.info"),
    },
    {
      key: "exploreChallenges" as const,
      enabled: enabledTasks.exploreChallenges,
      complete: viewedChallenge,
      to: "/challenges/discover",
      title: t("home.tasklist.exploreChallenges"),
      subtitle: t("home.tasklist.exploreChallenges.info"),
    },
    {
      key: "createIdea" as const,
      enabled: enabledTasks.createIdea,
      complete: !!createdIdea,
      to: "/challenges/discover",
      title: t("home.tasklist.createIdea"),
      subtitle: t("home.tasklist.createIdea.info"),
    },
    {
      key: "submitIdea" as const,
      enabled: enabledTasks.submitIdea,
      complete: !!submittedIdea,
      to: "/ideas",
      title: t("home.tasklist.submitIdea"),
      subtitle: t("home.tasklist.submitIdea.info"),
    },
    {
      key: "joinGroup" as const,
      enabled: enabledTasks.joinGroup,
      complete: joinedGroup,
      to: "/groups",
      title: t("home.tasklist.joinGroup"),
      subtitle: t("home.tasklist.joinGroup.info"),
    },
    {
      key: "setup2fa" as const,
      enabled: enabledTasks.setup2fa && hasPassword,
      complete: setup2fa,
      to: "/preferences/password",
      title: t("home.tasklist.setup2fa"),
      subtitle: t("home.tasklist.setup2fa.info"),
    },
  ].filter((task) => task.enabled);

  if (!tasks.length || tasks.every((task) => task.complete)) return null;

  const columns = tasks.length > 3 ? 2 : 1;

  return (
    <HomeBlock $width={columns}>
      <H3>
        {t("home.getStarted.title")}, {user.profile?.firstName}
        <Subheader>{t("home.getStarted.info")}</Subheader>
      </H3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            theme.sizes.isComputer && columns === 2 ? "1fr 1fr" : "1fr",
          gap: 12,
        }}
      >
        {tasks.map((task) => (
          <Link key={task.key} to={task.to} style={{ display: "flex" }}>
            <TaskItem>
              <Button
                color="primary"
                variant={task.complete ? "soft" : "outline"}
              >
                <Icon icon={task.complete ? CheckIcon : ArrowRightIcon} />
              </Button>
              <TaskItemContent>
                <H4>
                  {task.title}
                  <Subheader>{task.subtitle}</Subheader>
                </H4>
              </TaskItemContent>
            </TaskItem>
          </Link>
        ))}
      </div>
    </HomeBlock>
  );
};

export default TasklistBlock;
